import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { featureProductsKeys } from "@/application/query-keys/feature-products.keys";
import {
  featureProductsRepository,
  type UpsertFeatureProductInput,
} from "@/infrastructure/repositories/feature-products.supabase.repository";
import type { FeatureProductRow } from "@/infrastructure/supabase/database.types";

export function useAllFeatureProducts() {
  return useQuery({
    queryKey: featureProductsKeys.list(),
    queryFn: () => featureProductsRepository.list(),
  });
}

export function useProductFeatureCoverage(productId: string | undefined) {
  return useQuery({
    queryKey: featureProductsKeys.byProduct(productId ?? ""),
    queryFn: () =>
      productId ? featureProductsRepository.listByProduct(productId) : Promise.resolve([]),
    enabled: typeof productId === "string" && productId.length > 0,
  });
}

export function useUpsertFeatureProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpsertFeatureProductInput) => featureProductsRepository.upsert(input),
    onMutate: async (input) => {
      await qc.cancelQueries({ queryKey: featureProductsKeys.all });
      const listKey = featureProductsKeys.list();
      const byProductKey = featureProductsKeys.byProduct(input.product_id);
      const previousList = qc.getQueryData<FeatureProductRow[]>(listKey);
      const previousByProduct = qc.getQueryData<FeatureProductRow[]>(byProductKey);

      const now = new Date().toISOString();
      const optimistic: FeatureProductRow = {
        id: `optimistic-${input.product_id}-${input.feature_id}`,
        product_id: input.product_id,
        feature_id: input.feature_id,
        coverage: input.coverage,
        notes_md: input.notes_md,
        effort_estimate_weeks: input.effort_estimate_weeks,
        created_at: now,
        updated_at: now,
      };

      const replace = (current: FeatureProductRow[] | undefined) => {
        const base = current ?? [];
        const filtered = base.filter(
          (r) => !(r.product_id === input.product_id && r.feature_id === input.feature_id)
        );
        return [optimistic, ...filtered];
      };
      qc.setQueryData<FeatureProductRow[]>(listKey, replace);
      qc.setQueryData<FeatureProductRow[]>(byProductKey, replace);

      return { previousList, previousByProduct };
    },
    onError: (_err, input, ctx) => {
      if (ctx?.previousList) {
        qc.setQueryData(featureProductsKeys.list(), ctx.previousList);
      }
      if (ctx?.previousByProduct) {
        qc.setQueryData(featureProductsKeys.byProduct(input.product_id), ctx.previousByProduct);
      }
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: featureProductsKeys.all });
    },
  });
}

export function useRemoveFeatureProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ productId, featureId }: { productId: string; featureId: string }) =>
      featureProductsRepository.remove(productId, featureId),
    onMutate: async ({ productId, featureId }) => {
      await qc.cancelQueries({ queryKey: featureProductsKeys.all });
      const listKey = featureProductsKeys.list();
      const byProductKey = featureProductsKeys.byProduct(productId);
      const previousList = qc.getQueryData<FeatureProductRow[]>(listKey);
      const previousByProduct = qc.getQueryData<FeatureProductRow[]>(byProductKey);

      const drop = (current: FeatureProductRow[] | undefined) =>
        (current ?? []).filter((r) => !(r.product_id === productId && r.feature_id === featureId));
      qc.setQueryData<FeatureProductRow[]>(listKey, drop);
      qc.setQueryData<FeatureProductRow[]>(byProductKey, drop);

      return { previousList, previousByProduct };
    },
    onError: (_err, { productId }, ctx) => {
      if (ctx?.previousList) {
        qc.setQueryData(featureProductsKeys.list(), ctx.previousList);
      }
      if (ctx?.previousByProduct) {
        qc.setQueryData(featureProductsKeys.byProduct(productId), ctx.previousByProduct);
      }
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: featureProductsKeys.all });
    },
  });
}
